import { Box, Typography } from "@mui/material";
import { CategoryTitle } from "components/search/input/DifferencePanel/common";
import { MassDiff } from "components/search/input/DifferencePanel/MassDiff";
import { TFunction } from "i18next";
import {
  NameBasedMaterialLimit,
  NameBasedMaterialLimitBounds,
} from "store/api/generatedApi";

type BoundField = "min_mass" | "soft_min_mass" | "soft_max_mass" | "max_mass";

const BOUND_FIELDS: readonly BoundField[] = [
  "min_mass",
  "soft_min_mass",
  "soft_max_mass",
  "max_mass",
] as const;

const BOUND_FIELD_LABELS: Record<BoundField, string> = {
  min_mass: "min",
  soft_min_mass: "softMin",
  soft_max_mass: "softMax",
  max_mass: "max",
};

const findBound = (
  limit: NameBasedMaterialLimit | null | undefined,
  basketNumber: number | null
): NameBasedMaterialLimitBounds | undefined =>
  limit?.bounds.find((bound) => bound.basket_number === basketNumber);

const collectBasketNumbers = (
  limits: (NameBasedMaterialLimit | null | undefined)[]
): (number | null)[] => {
  const seen = new Set<string>();
  const order: (number | null)[] = [];
  for (const limit of limits) {
    for (const bound of limit?.bounds ?? []) {
      const basketKey =
        bound.basket_number === null ? "null" : String(bound.basket_number);
      if (!seen.has(basketKey)) {
        seen.add(basketKey);
        order.push(bound.basket_number);
      }
    }
  }
  return [...order].sort((a, b) => {
    if (a === null) return -1;
    if (b === null) return 1;
    return a - b;
  });
};

const basketLabel = (basketNumber: number | null, t: TFunction): string =>
  basketNumber === null ? t("mix") : `${t("basket")} ${basketNumber}`;

interface BoundsGridProps {
  previous: NameBasedMaterialLimit | null;
  updated: NameBasedMaterialLimit | null;
  t: TFunction;
  massUnit: string;
  format: (value: number) => string;
}

export const BoundsGrid = ({
  previous,
  updated,
  t,
  massUnit,
  format,
}: BoundsGridProps) => {
  const basketNumbers = collectBasketNumbers([previous, updated]);
  if (basketNumbers.length === 0) return null;
  return (
    <Box>
      <CategoryTitle>{t("bounds")}</CategoryTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto repeat(4, 1fr)",
          columnGap: 1,
          rowGap: 0.5,
          alignItems: "center",
          mt: 0.5,
        }}
      >
        <Box />
        {BOUND_FIELDS.map((field) => (
          <Typography
            key={field}
            variant="caption"
            sx={{ textAlign: "center" }}
          >
            {t(BOUND_FIELD_LABELS[field])}
          </Typography>
        ))}
        {basketNumbers.map((basketNumber) => {
          const previousBound = findBound(previous, basketNumber);
          const updatedBound = findBound(updated, basketNumber);
          return (
            <Box key={basketNumber ?? "mix"} sx={{ display: "contents" }}>
              <Typography variant="body2">
                {basketLabel(basketNumber, t)}
              </Typography>
              {BOUND_FIELDS.map((field) => (
                <Box key={field} sx={{ textAlign: "center" }}>
                  <MassDiff
                    previous={previousBound?.[field]}
                    updated={updatedBound?.[field]}
                    massUnit={massUnit}
                    format={format}
                  />
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
